/**
 * Dashboard Store — Donor Portal
 *
 * Caches donation KPI stats for the logged-in donor so repeated
 * navigations to the dashboard do not trigger redundant API calls.
 */
import { defineStore } from 'pinia';
import { createDashboardService } from '@/modules/dashboard/services/dashboard.service';
import type { DonorKpiData } from '@/modules/dashboard/types';
import { config } from '@/core/config/app.config';

const service = createDashboardService();

interface DashboardState {
  kpi: DonorKpiData;
  isLoading: boolean;
  loadedForPersonId: string | null;
}

const DEFAULT_KPI: DonorKpiData = {
  totalDonations: 0,
  totalVolumeMl: 0,
  volumeUsedMl: 0,
  babiesHelped: 0,
  activeDays: null,
};

export const useDashboardStore = defineStore('dashboard', {
  state: (): DashboardState => ({
    kpi: { ...DEFAULT_KPI },
    isLoading: false,
    loadedForPersonId: null,
  }),

  actions: {
    async loadStats(): Promise<void> {
      const currentPersonId = config.personId;
      if (this.loadedForPersonId === currentPersonId) return;
      this.isLoading = true;
      try {
        this.kpi = await service.fetchDonationStats();
        this.loadedForPersonId = currentPersonId;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
